/**
 * SchedulesEditor.tsx — FR-013 (H4) / FR-014 (Twin #1) / T314 rewrite.
 *
 * Bindings:
 *   - api.schedules.markAsReady      — REAL twin (convex/schedules.ts:421)
 *   - api.schedules.duplicateSchedule — REAL (orphan #3, plan §3 Wave 3h)
 *   - api.schedules.scheduleForFuture — REAL (orphan #4, plan §3 Wave 3h)
 *   - api.schedules.getScheduleWithItems — REAL query (convex/schedules.ts:76)
 *
 * The stub-side namesake `api.schedule.markAsReady` (singular, at
 * convex/schedule.ts:1098) is intentionally NOT referenced anywhere
 * in this file — see specs/exploration/stub_orphan_inventory.md §4
 * Twin #1, and the negative assertion in
 * frontend-v3/tests/destub/twins.test.ts (T-C).
 *
 * Every mutation call site is routed through `useMutationWithToast`
 * (FR-013 (H1)): the wrapper logs argument KEYS only (never values),
 * re-throws after toasting, and never reports a false success.
 *
 * H4 ratchet: this file MUST NOT be a thin re-export shell. See
 * frontend-v3/tests/destub/honest-state.test.ts (H4) and the sibling
 * OrgSettings.tsx for the matching twin rewrite.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AccessGate,
  Breadcrumbs,
  Btn,
  EmptyState,
  Field,
  Input,
  Modal,
  Panel,
  Pill,
  Select,
  Skeleton,
  StatCard,
  Thumb,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { useChannelList } from '../../hooks/useChannelList';
import { api } from '@convex/api';
// T318 — the mock fixture import retired; the channel select binds to the
// real `useChannelList()` snapshot and renders an honest empty option
// list while the subscription resolves. No fixture dependency remains.
import { fmtDur } from '../../lib/format';

interface SchedulesEditorProps {
  id?: string;
}

export function SchedulesEditor({ id }: SchedulesEditorProps) {
  // ──────────────────────────────────────────────────────────────────
  // Real Convex query — the container + its items.
  // ──────────────────────────────────────────────────────────────────
  // The route binds `:id` to the Convex `schedules` row id; we only
  // call the query when the id is present (otherwise the page renders
  // the empty-state path for "Nueva programación").
  const scheduleDoc = useQuery(
    api.schedules.getScheduleWithItems,
    id ? { scheduleId: id as any } : 'skip',
  );

  // T318 — wired channel list for the "Asignar a canal" select. Returns
  // `undefined` while loading (honest: select shows no options yet); never
  // fabricated. The real Convex `_id` is the option value so future-schedule
  // submissions carry a real id, not a mock string.
  const wiredChannels = useChannelList();

  // ──────────────────────────────────────────────────────────────────
  // Real mutations — every one wrapped, every one fail-loud on error.
  // ──────────────────────────────────────────────────────────────────
  const markAsReadyM = useMutationWithToast(api.schedules.markAsReady, {
    logLabel: 'schedules_markAsReady',
    successMessage: 'Programación marcada como lista.',
    errorMessagePrefix: 'No se pudo marcar como lista',
  });
  const duplicateScheduleM = useMutationWithToast(
    api.schedules.duplicateSchedule,
    {
      logLabel: 'schedules_duplicateSchedule',
      successMessage: 'Programación duplicada.',
      errorMessagePrefix: 'No se pudo duplicar la programación',
    },
  );
  const scheduleForFutureM = useMutationWithToast(
    api.schedules.scheduleForFuture,
    {
      logLabel: 'schedules_scheduleForFuture',
      successMessage: 'Programación agendada.',
      errorMessagePrefix: 'No se pudo agendar la programación',
    },
  );

  // ──────────────────────────────────────────────────────────────────
  // Local UI state (modal, channel selection for publish dialog).
  // ──────────────────────────────────────────────────────────────────
  const [pubOpen, setPubOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [futureMs, setFutureMs] = useState<string>('');

  // ──────────────────────────────────────────────────────────────────
  // Virtualization (NFR-004): windows the timeline list once the row
  // count crosses ~300. The virtualizer ref is bound to the scroll
  // parent below; estimateSize matches the per-row block (~58 px:
  // 12 px padding top + 14 px gap + 30 px content row + 12 px padding
  // bottom). The overscan keeps a small buffer above/below the visible
  // window so fast scrolling doesn't show a blank gap.
  // ──────────────────────────────────────────────────────────────────
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);

  // ──────────────────────────────────────────────────────────────────
  // Loading / honest-empty handling.
  // ──────────────────────────────────────────────────────────────────
  if (id && scheduleDoc === undefined) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Skeleton h={60} />
        <Skeleton h={260} />
      </div>
    );
  }
  if (id && scheduleDoc === null) {
    return (
      <EmptyState
        icon="calendar"
        title="Programación no encontrada"
        body="No pudimos cargar esta programación. Puede haber sido eliminada."
      />
    );
  }

  const name: string = scheduleDoc?.name ?? 'Nueva programación';
  const items: any[] = scheduleDoc?.items ?? [];
  const totalMs: number = items.reduce(
    (sum: number, it: any) => sum + (it?.durationMs ?? 0),
    0,
  );

  // NFR-004 virtualizer — only the visible window plus overscan is
  // rendered into the DOM, keeping the timeline smooth at 400+ rows.
  // initialRect provides a sane window size for environments (jsdom in
  // tests, very first paint) where getBoundingClientRect() reports zero
  // height — without it, getVirtualItems() returns [] and the test would
  // not be able to observe windowing.
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => timelineScrollRef.current,
    estimateSize: () => 58,
    overscan: 8,
    initialRect: { width: 800, height: 480 },
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  // ──────────────────────────────────────────────────────────────────
  // Real call-sites (FR-014 Twin #1 binds api.schedules.markAsReady).
  // ──────────────────────────────────────────────────────────────────
  const handleMarkAsReady = () => {
    if (!id) return;
    void markAsReadyM.run({ scheduleId: id as any });
  };
  const handleDuplicate = () => {
    if (!id) return;
    void duplicateScheduleM.run({ scheduleId: id as any });
  };
  const handleScheduleForFuture = () => {
    if (!id || !selectedChannel) return;
    const startTime = Number(futureMs);
    if (!Number.isFinite(startTime)) return;
    void scheduleForFutureM.run({
      scheduleId: id as any,
      channelId: selectedChannel as any,
      startTime,
    });
  };

  const isBusy =
    markAsReadyM.loading ||
    duplicateScheduleM.loading ||
    scheduleForFutureM.loading;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <Breadcrumbs
            items={[
              { label: 'Programaciones', href: '#/schedules' },
              { label: name },
            ]}
          />
          <h1
            style={{
              margin: '10px 0 0',
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              fontWeight: 400,
              letterSpacing: '-0.4px',
            }}
          >
            {name}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <AccessGate perm="schedule_publish">
            <Btn
              variant="ghost"
              icon="copy"
              onClick={handleDuplicate}
              disabled={!id || isBusy}
            >
              Duplicar
            </Btn>
          </AccessGate>
          <AccessGate perm="schedule_publish">
            <Btn
              variant="ghost"
              icon="checkCircle"
              onClick={handleMarkAsReady}
              disabled={!id || isBusy}
            >
              Marcar como lista
            </Btn>
          </AccessGate>
          <AccessGate perm="schedule_publish">
            <Btn
              variant="solid"
              icon="send"
              onClick={() => setPubOpen(true)}
              disabled={!id || isBusy}
            >
              Publicar
            </Btn>
          </AccessGate>
        </div>
      </div>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label="Duración total"
          value={fmtDur(totalMs)}
          icon="clock"
        />
        <StatCard
          label="Elementos"
          value={items.length}
          hint="en la parrilla"
          icon="list"
        />
        <StatCard
          label="Estado"
          value={scheduleDoc?.status ?? '—'}
          icon="info"
        />
        <StatCard
          label="Canal"
          value={scheduleDoc?.channelId ? 'Asignado' : 'Sin asignar'}
          icon="tv"
        />
      </div>
      <Panel
        title="Línea de tiempo"
        right={
          <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
            Total: {fmtDur(totalMs)} · {items.length} elementos
          </span>
        }
      >
        {items.length === 0 ? (
          <EmptyState
            icon="calendar"
            title="Sin elementos"
            body="Esta programación aún no tiene elementos."
          />
        ) : (
          <div
            ref={timelineScrollRef}
            style={{ maxHeight: 480, overflowY: 'auto' }}
          >
            <div
              style={{
                position: 'relative',
                height: rowVirtualizer.getTotalSize(),
                width: '100%',
              }}
            >
              {virtualRows.map((vr) => {
                const it: any = items[vr.index] ?? {};
                const i = vr.index;
                return (
                  <div
                    key={it._id ?? i}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      transform: `translateY(${vr.start}px)`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '12px 0',
                      borderBottom:
                        i < items.length - 1
                          ? '1px solid var(--lightest-gray)'
                          : 'none',
                    }}
                  >
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 12,
                        color: 'var(--muted-slate)',
                        width: 56,
                      }}
                    >
                      #{(it.sequenceNumber ?? i) + 1}
                    </span>
                    <div style={{ width: 64, flexShrink: 0 }}>
                      <Thumb radius={6} />
                    </div>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 14,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.title ?? 'Sin título'}
                    </span>
                    <Pill tone="neutral" size="sm">
                      {fmtDur(it.durationMs ?? 0)}
                    </Pill>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Panel>

      <Modal
        open={pubOpen}
        onClose={() => setPubOpen(false)}
        title="Agendar programación"
        width={560}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setPubOpen(false)}>
              Cancelar
            </Btn>
            <Btn
              variant="solid"
              onClick={() => {
                handleScheduleForFuture();
                setPubOpen(false);
              }}
              disabled={!selectedChannel || !futureMs || isBusy}
            >
              Agendar
            </Btn>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Asignar a canal">
            <Select
              value={selectedChannel}
              onChange={setSelectedChannel}
              full
              options={(wiredChannels ?? []).map((c) => c._id)}
            />
          </Field>
          <Field label="Hora de inicio (epoch ms)">
            <Input value={futureMs} onChange={setFutureMs} />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

export default SchedulesEditor;
